/*
 * @Author: Yun
 * @Date: 2025-12-04
 * @Last Modified by: Yun
 * @Last Modified time: 2025-12-04
 */

import React, { useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import styles from './Modal.module.scss';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  originPosition?: { x: number; y: number } | null;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, originPosition }) => {
  const modalContainerRef = React.useRef<HTMLDivElement>(null);
  // ESC 键关闭
  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // 动画执行标志
  const animationExecutedRef = React.useRef(false);

  // ESC 键监听
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isOpen, handleEscKey]);

  // 弹出动画 - 只在打开时执行一次
  useEffect(() => {
    if (isOpen && modalContainerRef.current && originPosition && !animationExecutedRef.current) {
      animationExecutedRef.current = true;
      
      const container = modalContainerRef.current;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      
      // 从点击位置到中心的偏移
      const deltaX = originPosition.x - centerX;
      const deltaY = originPosition.y - centerY;
      
      // 设置初始位置和缩放
      gsap.set(container, {
        x: deltaX,
        y: deltaY,
        scale: 0.3,
        opacity: 0,
      });
      
      // 弹出动画
      gsap.to(container, {
        x: 0,
        y: 0,
        scale: 1,
        opacity: 1,
        duration: 0.5,
        ease: 'back.out(1.4)',
      });
    }
    
    // 关闭时重置标志
    if (!isOpen) {
      animationExecutedRef.current = false;
    }
  }, [isOpen, originPosition]);

  // 点击遮罩层关闭（带动画）
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // 关闭动画
  const handleClose = () => {
    if (modalContainerRef.current && originPosition) {
      const container = modalContainerRef.current;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const deltaX = originPosition.x - centerX;
      const deltaY = originPosition.y - centerY;
      
      gsap.to(container, {
        x: deltaX,
        y: deltaY,
        scale: 0.3,
        opacity: 0,
        duration: 0.4,
        ease: 'back.in(1.4)',
        onComplete: onClose,
      });
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div ref={modalContainerRef} className={styles.modalContainer}>
        <button className={styles.closeButton} onClick={handleClose} aria-label="Close modal">
          ×
        </button>
        <div className={styles.modalContent}>{children}</div>
      </div>
    </div>
  );
};

export default Modal;
